import { useEffect, useRef, useState } from 'react';
import type { ZoomRange } from '@/camera/useLocalCamera';

const RANGE_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

const zoomText = (v: number) => `${Math.round(v * 10) / 10}×`;

/**
 * Shows the value while dragging; `onCommit` runs on release, not on every step. A commit that
 * resolves to false puts the slider back on `value`.
 */
export function ZoomSlider({
  min,
  max,
  step,
  value,
  onCommit,
}: ZoomRange & {
  value: number;
  onCommit: (value: number) => unknown;
}) {
  const clamped = Math.min(max, Math.max(min, value));
  const [shown, setShown] = useState(clamped);
  const input = useRef<HTMLInputElement>(null);
  const latest = useRef({ clamped, onCommit });
  latest.current = { clamped, onCommit };

  useEffect(() => setShown(clamped), [clamped]);
  // The native change event fires on release for a pointer but on every step for the keyboard,
  // so a key commits when it is let go.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    let keyDown = false;
    let pending = false;
    const commit = () => {
      pending = false;
      void Promise.resolve(latest.current.onCommit(Number(el.value))).then((ok) => {
        if (ok === false) setShown(latest.current.clamped);
      });
    };
    const onChange = () => {
      if (keyDown) pending = true;
      else commit();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (RANGE_KEYS.has(e.key)) keyDown = true;
    };
    const onKeyUp = () => {
      keyDown = false;
      if (pending) commit();
    };
    el.addEventListener('change', onChange);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', onKeyUp);
    el.addEventListener('blur', onKeyUp);
    return () => {
      el.removeEventListener('change', onChange);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('keyup', onKeyUp);
      el.removeEventListener('blur', onKeyUp);
    };
  }, []);

  return (
    <label className="flex min-h-11 items-center gap-3 text-base text-content-primary">
      Zoom
      <input
        ref={input}
        type="range"
        aria-label="Zoom"
        aria-valuetext={zoomText(shown)}
        className="min-h-11 min-w-0 flex-1"
        min={min}
        max={max}
        step={step}
        value={shown}
        onChange={(e) => setShown(Number(e.target.value))}
      />
      <span className="w-12 text-right tabular-nums">{zoomText(shown)}</span>
    </label>
  );
}
